import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { Loader2, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [projectId, setProjectId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const startDemo = async () => {
      try {
        const res = await fetch(`${API}/api/demo/start`, { method: "POST" });
        if (!res.ok) throw new Error("Failed to start demo session");
        const data = await res.json();

        setAuth(data.access_token, data.user);
        setProjectId(data.project_id);
        setStatus("ready");
      } catch (err) {
        setError(err.message);
        setStatus("error");
      }
    };
    startDemo();
  }, [setAuth]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="demo-page">
      <div className="max-w-lg w-full">
        {status === "loading" && (
          <div className="text-center space-y-4" data-testid="demo-loading">
            <Loader2 className="w-10 h-10 text-[#00f0ff] animate-spin mx-auto" />
            <h2 className="text-xl font-bold text-white font-mono">Setting up your sandbox...</h2>
            <p className="text-sm text-zinc-500 font-mono">Creating demo session with Business-tier access</p>
          </div>
        )}

        {status === "ready" && (
          <div className="bg-zinc-900/60 border border-zinc-700/50 rounded-xl p-8 space-y-6" data-testid="demo-ready">
            <div className="text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 text-[#00f0ff] mx-auto" />
              <h2 className="text-2xl font-bold text-white font-mono">Sandbox Ready</h2>
              <p className="text-sm text-zinc-400 font-mono">
                Your demo environment is set up with a pre-built AWS e-commerce architecture and full Business-tier access.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                className="w-full bg-[#00f0ff] text-black font-bold font-mono hover:bg-[#00f0ff]/90 transition-all"
                onClick={() => navigate(`/wizard/${projectId}`)}
                data-testid="demo-explore-project-btn"
              >
                Explore Demo Project <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 font-mono hover:bg-zinc-800"
                onClick={() => navigate("/scan")}
                data-testid="demo-try-byoc-btn"
              >
                Try BYOC Scanner
              </Button>

              <Button
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 font-mono hover:bg-zinc-800"
                onClick={() => window.open(`${API}/api/demo/guide`, "_blank")}
                data-testid="demo-guide-btn"
              >
                Open Demo Guide <ExternalLink className="w-3.5 h-3.5 ml-2" />
              </Button>
            </div>

            <div className="pt-4 border-t border-zinc-800 text-center">
              <p className="text-xs text-zinc-600 font-mono">
                Demo data resets periodically. <a href="/" className="text-[#00f0ff] hover:underline">Create an account</a> to save your work.
              </p>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center space-y-4" data-testid="demo-error">
            <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
              <span className="text-red-400 text-xl">!</span>
            </div>
            <h2 className="text-xl font-bold text-white font-mono">Setup Failed</h2>
            <p className="text-sm text-zinc-400 font-mono">{error}</p>
            <Button
              className="bg-zinc-800 text-white font-mono hover:bg-zinc-700"
              onClick={() => { setStatus("loading"); window.location.reload(); }}
            >
              Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
